import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { isBalanceStaleForDisplay } from "./engineBalance";
import { cn } from "../client/utils";

// Réserve de fraîcheur sur un solde de compte SYNCHRONISÉ.
//
// POURQUOI CE COMPOSANT EXISTE
// Le solde broker (`brokerBalance`) fait autorité sur l'affichage dès qu'il
// existe, et il est relu au plus une fois par heure (quota Tradovate : 600 req/h
// par IP PARTAGÉE). Quand cette lecture échoue — Tradovate répond
// épisodiquement 408 sur getcashbalancesnapshot — la dernière valeur connue
// reste affichée, telle quelle, sans la moindre réserve. L'utilisateur lisait
// donc un solde potentiellement vieux de plusieurs heures présenté comme
// actuel, sur un compte prop firm où ce chiffre décide d'un breach.
//
// `brokerBalanceAt` était déjà calculé, déjà exposé au client par
// accountEnrichment.ts, et documenté dans schema.prisma comme servant
// précisément à « le dire à l'utilisateur plutôt que de faire passer une valeur
// ancienne pour fraîche ». Aucun composant ne le lisait. Ce composant est ce
// chaînon manquant.
//
// CE QU'IL NE FAIT PAS
// Il ne change JAMAIS le chiffre affiché. Substituer silencieusement le solde
// dérivé sous les yeux de l'utilisateur serait une seconde information
// trompeuse, pas un correctif — le seuil de bascule est donc côté lisibilité,
// pas côté valeur.

export default function BalanceFreshnessHint({
  brokerBalance,
  brokerBalanceAt,
  className,
}: {
  brokerBalance: number | null | undefined;
  brokerBalanceAt: Date | string | null | undefined;
  className?: string;
}) {
  const t = useTranslation();
  const { language } = useLanguageContext();

  // Les dates traversent SuperJSON : selon l'écran, `brokerBalanceAt` arrive en
  // Date ou en string ISO. On normalise ici plutôt que dans chaque appelant.
  const at =
    brokerBalanceAt === null || brokerBalanceAt === undefined
      ? null
      : brokerBalanceAt instanceof Date
        ? brokerBalanceAt
        : new Date(brokerBalanceAt);

  const stale = isBalanceStaleForDisplay({
    brokerBalance: brokerBalance ?? null,
    brokerBalanceAt: at,
    now: new Date(),
  });

  // Régime nominal, ou compte manuel : rien à signaler. Une mention permanente
  // s'apprend à s'ignorer, y compris le jour où elle est vraie.
  if (!stale) return null;

  // Sans horodatage exploitable, l'âge est INCONNU — on ne peut pas écrire
  // « il y a X ». Le dire franchement vaut mieux qu'inventer une durée.
  const label =
    at === null || Number.isNaN(at.getTime())
      ? t.tradingAccounts.balanceStaleUnknown
      : `${t.tradingAccounts.balanceStalePrefix} ${formatDistanceToNow(at, {
          addSuffix: true,
          locale: language === "fr" ? fr : undefined,
        })}`;

  return (
    <span
      title={t.tradingAccounts.balanceStaleHelp}
      className={cn(
        "inline-flex items-center gap-1 text-[10px] font-medium text-amber-500 whitespace-nowrap",
        className,
      )}
    >
      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
      {label}
    </span>
  );
}
